import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeadCashes from "./headCashes";
import DetailCashExtraction from "./detailCashExtraction";
import { Box, Button } from "@mui/material";
import { FormActions } from "../../components/ui/form";
import { ErrorModal } from "../../components/ModalAlert";
import supabase from "../../supabase";
import { LoaderSpinner } from "../../components/spinner/loader";
import { logCheckStateChange } from "../../utils/checkStateLogger";
import { syncChequesCashBalance } from "../../utils/chequesCash";

const empty = (value) => value === undefined || value === null || value === '';

const CashExtraction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state;
  const [loading, setLoading] = useState(false);
  const [usedBalance, setUsedBalance] = useState(0);
  const [description, setDescription] = useState('');
  const [amountToWithdraw, setAmountToWithdraw] = useState('');
  const [cashIdSelected, setCashIdSelected] = useState('');
  const [cashId] = useState(1);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [checks, setChecks] = useState([]);
  const [cashes, setCashes] = useState([]);
  const [balance, setBalance] = useState('');
  const [cashesExtraction, setCashesExtraction] = useState([]);
  const [selectedChecksNumbers, setSelectedChecksNumbers] = useState('');
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [cashTotalAmount, setCashTotalAmount] = useState(0);
  const [checkDestination, setCheckDestination] = useState('');
  // Elimino validateFields y errorMsg

  const handleAmountToWithdrawChange = (newAmountToWithdraw) => setAmountToWithdraw(newAmountToWithdraw);
  
  const handleCashIdSelectedChange = (newCashIdSelected) => {
    setCashIdSelected(newCashIdSelected);
    // Buscar el saldo de la caja seleccionada
    const selectedCash = cashesExtraction.find(cash => cash.cashId == newCashIdSelected);
    if (selectedCash) {
      setCashTotalAmount(parseFloat(selectedCash.balance).toFixed(2));
    } else {
      setCashTotalAmount(0);
    }
  };
  
  const handleDescriptionChange = (newDescription) => setDescription(newDescription);
  const handleCheckDestinationChange = (newDestination) => setCheckDestination(newDestination);

  const updateCashesExtraction = (newDetails) => {
    setCashesExtraction(newDetails);
    // Si ya hay una caja seleccionada, actualizar el monto total
    if (cashIdSelected) {
      const selectedCash = newDetails.find(cash => cash.cashId == cashIdSelected);
      if (selectedCash) {
        setCashTotalAmount(parseFloat(selectedCash.balance));
      }
    }
  };

  // Restaurar función de validación original
  const allInputsValid = () => {
    const amountValue = parseFloat(amountToWithdraw);
    const totalValue = parseFloat(cashTotalAmount);
    const errors = [];

    if (amountValue <= 0 || amountToWithdraw === '') {
      errors.push('Debe ingresar un monto a retirar');
    }
    if (description === '') {
      errors.push('Debe ingresar una descripción');
    }
    if (cashIdSelected === '') {
      errors.push('Debe seleccionar una caja');
    }
    if (amountValue > totalValue) {
      errors.push(`El monto a retirar (${amountValue}) no puede exceder el saldo disponible (${totalValue})`);
    }
    if (cashIdSelected === '7' && (!checkDestination || checkDestination === 'Seleccionar Destino')) {
      errors.push('Debe seleccionar un destino para el cheque');
    }
    if (cashIdSelected === '7' && selectedChecks.length === 0) {
      errors.push('Debe seleccionar al menos un cheque');
    }

    if (errors.length > 0) {
      ErrorModal({ 
        title: "Error", 
        text: errors.join('. ') 
      });
      return false;
    }
    return true;
  };

  const saveOperation = async () => {
    setLoading(true);
    if (allInputsValid()) {
      try {
        const destinoLabel = checkDestination === 'cartera' ? 'Sale de Cartera' : (checkDestination === 'efectivo' ? 'Efectivo' : checkDestination === 'banco' ? 'Banco' : checkDestination);
        const amountValue = parseFloat(amountToWithdraw) * -1;

        const cashMovementsToInsert = {
          amount: amountValue,
          description: cashIdSelected === '7'
            ? (selectedChecksNumbers
                ? `${description} [ ${selectedChecksNumbers} ] - Destino: ${destinoLabel}`
                : `${description} - Destino: ${destinoLabel}`)
            : description,
          paymentMethod: -Number(cashIdSelected),
          createdAt: new Date(),
          cashId: cashIdSelected
        };

        await supabase
          .from("cashMovements")
          .insert(cashMovementsToInsert);

        if (cashIdSelected === '7' && selectedChecks.length > 0) {
          const selectedCheckIds = selectedChecks.map((check) => check.paymentDetailsIdid);
          const { error: checkStateError } = await supabase
            .from("paymentDetails")
            .update({ checkState: 1 })
            .in("paymentDetailsIdid", selectedCheckIds);

          if (checkStateError) {
            throw checkStateError;
          }

          await Promise.all(
            selectedChecks.map((check) =>
              logCheckStateChange({
                paymentDetailsId: check.paymentDetailsIdid,
                previousState: 0,
                newState: 1,
                source: "cashExtraction",
                checkNumber: check.checkNumber || null,
              })
            )
          );
        }

        if (cashIdSelected === '7') {
          await syncChequesCashBalance();
        } else {
          const { data: cashData } = await supabase
            .from("cash")
            .select("balance")
            .eq("cashId", cashMovementsToInsert.cashId)
            .single();

          const updatedBalance = parseFloat(cashData.balance) + amountValue;

          await supabase
            .from("cash")
            .update({ balance: updatedBalance })
            .eq("cashId", cashMovementsToInsert.cashId);
        }

        // Si el destino es efectivo o banco, crear un movimiento de ingreso
        if (checkDestination.toLowerCase() === 'efectivo' || checkDestination.toLowerCase() === 'banco') {
          const destinationCashId = checkDestination.toLowerCase() === 'efectivo' ? '1' : '2'; // 1 para efectivo, 2 para banco
          
          const destinationMovement = {
            amount: amountToWithdraw,
            description: selectedChecksNumbers ? `Ingreso de cheque [ ${selectedChecksNumbers} ]` : `Ingreso de cheque`,
            paymentMethod: destinationCashId,
            createdAt: new Date(),
            cashId: destinationCashId
          };

          // Insertar el movimiento de ingreso
          await supabase
            .from("cashMovements")
            .insert(destinationMovement);

          // Actualizar el saldo de la caja destino
          const { data: destinationCashData } = await supabase
            .from("cash")
            .select("balance")
            .eq("cashId", destinationCashId)
            .single();

          const destinationUpdatedBalance = parseFloat(destinationCashData.balance) + parseFloat(amountToWithdraw);
          
          await supabase
            .from("cash")
            .update({ balance: destinationUpdatedBalance })
            .eq("cashId", destinationCashId);
        }

      } catch (error) {
        ErrorModal({ title: "Error", text: error.message });
      } finally {
        setLoading(false);
      }
      window.location.reload();
    }
    setLoading(false);
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
      <HeadCashes />
      <DetailCashExtraction
        onCashIdSelectedChange={handleCashIdSelectedChange}
        onAmountToWithdrawChange={handleAmountToWithdrawChange}
        onDescriptionChange={handleDescriptionChange}
        onCheckDestinationChange={handleCheckDestinationChange}
        updateCashesExtraction={updateCashesExtraction}
        setSelectedChecksNumbers={setSelectedChecksNumbers}
        setSelectedChecks={setSelectedChecks}
      />
      <FormActions>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveOperation}
          sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 200 }}
        >
          Guardar retiro
        </Button>
      </FormActions>
    </Box>
  );
};

export default CashExtraction;
